import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import Switch from "../../components/Switch";
import { useConfirm } from "../../components/ConfirmDialog";
import { INTENT } from "../../lib/helpers";

// Éditeur d'un workflow de prestation, en page entière (décisions du 01/10, ébauche « Éditeur de workflows en colonnes ») :
// - les phases côte à côte, comme la vue de l'entreprise (au moins 290 px, défilement de côté au-delà), une colonne
//   « Nouvelle phase » au bout ; sur téléphone, les phases s'empilent ;
// - des cartes d'étape compactes (nom, pastilles) ; un clic ouvre le panneau de l'étape : nom, description,
//   obligatoire, formulaire, déplacement, suppression ;
// - on glisse une phase par son en-tête, une étape d'une phase à l'autre (glisser-déposer du navigateur, souris
//   seulement) ; au doigt et au clavier, l'étape se déplace par son panneau (Monter, Descendre, Phase).
// Le formulaire choisi est copié dans chaque nouveau projet créé avec ce workflow (serveur : _build_phases).
const LIST = "/admin/automations-workflows-prestation";
const uid = () => `p_${Date.now()}${Math.floor(Math.random() * 100000)}`;

export default function ProjectWorkflowEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const isNew = id === "nouveau";
  const [meta, setMeta] = useState(null); // { id?, type, type_label }
  const [name, setName] = useState("");
  const [phases, setPhases] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState(null); // id local de l'étape ouverte dans le panneau
  const [drag, setDrag] = useState(null); // { kind: "phase", pi } | { kind: "etape", pi, ei }
  const [over, setOver] = useState(null); // { kind: "phase", pi } | { kind: "etape", pi, ei }
  const [newPhase, setNewPhase] = useState("");
  const [newEtape, setNewEtape] = useState({});
  const [saving, setSaving] = useState(false);
  const typeParam = sp.get("type");
  const loaded = useRef(null);

  // Chargement : une seule réponse appliquée, une seule fois par workflow. Une réponse arrivée après coup (effet relancé,
  // double montage du mode strict de React en développement) remettait l'éditeur à zéro sous les yeux, modifications perdues.
  useEffect(() => {
    const key = `${id}:${typeParam || ""}`;
    if (loaded.current === key) return undefined;
    let alive = true;
    Promise.all([api.get("/admin/project-workflows"), api.get("/admin/form-templates")]).then(([w, t]) => {
      if (!alive) return;
      loaded.current = key;
      setTemplates(t.data || []);
      if (isNew) {
        const same = (w.data || []).find((x) => x.type === typeParam);
        setMeta({ type: typeParam, type_label: same?.type_label || typeParam });
        return;
      }
      const wf = (w.data || []).find((x) => x.id === id);
      if (!wf) { toast.error("Workflow introuvable"); navigate(LIST); return; }
      setMeta({ id: wf.id, type: wf.type, type_label: wf.type_label });
      setName(wf.name);
      setPhases((wf.phases || []).map((ph) => ({
        id: uid(), name: ph.name,
        etapes: (ph.etapes || []).map((e) => ({ id: uid(), name: e.name, blocking: !!e.blocking, description: e.description || "", form_template_id: e.form_template_id || "" })),
      })));
    }).catch(() => { if (alive) toast.error("Chargement impossible"); });
    return () => { alive = false; };
  }, [id, isNew, typeParam, navigate]);

  // Modifications non enregistrées : le navigateur prévient avant de fermer l'onglet.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Échap ferme le panneau de l'étape.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  const edit = (fn) => { setPhases(fn); setDirty(true); };
  const tplOf = (tid) => templates.find((t) => t.id === tid);

  const addPhase = () => {
    if (!newPhase.trim()) return;
    edit((prev) => [...prev, { id: uid(), name: newPhase.trim(), etapes: [] }]);
    setNewPhase("");
  };
  const removePhase = async (pi) => {
    const ph = phases[pi];
    if (ph.etapes.length && !(await confirm({ title: `Supprimer la phase « ${ph.name || "sans nom"} » ?`,
      message: `Ses ${ph.etapes.length} étape(s) seront retirées du workflow.`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    if (ph.etapes.some((e) => e.id === selected)) setSelected(null);
    edit((prev) => prev.filter((_, i) => i !== pi));
  };
  const movePhase = (from, to) => {
    if (from === to) return;
    edit((prev) => { const a = [...prev]; const [it] = a.splice(from, 1); a.splice(to, 0, it); return a; });
  };
  const addEtape = (pi) => {
    const v = (newEtape[pi] || "").trim();
    if (!v) return;
    edit((prev) => prev.map((ph, i) => (i === pi ? { ...ph, etapes: [...ph.etapes, { id: uid(), name: v, blocking: false, description: "", form_template_id: "" }] } : ph)));
    setNewEtape({ ...newEtape, [pi]: "" });
  };
  // Déplace l'étape (from) devant la position ei de la phase pi ; ei = longueur de la phase pour la mettre au bout.
  const moveEtape = (from, to) => edit((prev) => {
    const next = prev.map((ph) => ({ ...ph, etapes: [...ph.etapes] }));
    const [it] = next[from.pi].etapes.splice(from.ei, 1);
    let ei = to.ei;
    if (from.pi === to.pi && from.ei < ei) ei -= 1;
    next[to.pi].etapes.splice(Math.max(0, Math.min(ei, next[to.pi].etapes.length)), 0, it);
    return next;
  });
  const setEtape = (eid, patch) => edit((prev) => prev.map((ph) => ({ ...ph, etapes: ph.etapes.map((e) => (e.id === eid ? { ...e, ...patch } : e)) })));
  const removeEtape = (eid) => { setSelected(null); edit((prev) => prev.map((ph) => ({ ...ph, etapes: ph.etapes.filter((e) => e.id !== eid) }))); };

  const leave = async () => {
    if (dirty && !(await confirm({ title: "Quitter sans enregistrer ?", message: "Les modifications de ce workflow seront perdues.",
      confirmLabel: "Quitter sans enregistrer", cancelLabel: "Continuer", tone: "danger" }))) return;
    navigate(LIST);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Donnez un nom au workflow");
    const clean = phases.map((ph) => ({
      name: ph.name,
      etapes: ph.etapes.map((e) => ({ name: e.name, blocking: e.blocking, description: e.description || "", ...(e.form_template_id ? { form_template_id: e.form_template_id } : {}) })),
    }));
    if (!clean.some((ph) => ph.etapes.length > 0)) return toast.error("Ajoutez au moins une étape");
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/project-workflows", { type: meta.type, name: name.trim(), phases: clean });
      else await api.put(`/admin/project-workflows/${meta.id}`, { name: name.trim(), phases: clean });
      setDirty(false);
      toast.success("Workflow enregistré");
      navigate(LIST);
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  // Glisser-déposer (souris) : une phase par son en-tête, une étape devant une autre ou au bout d'une phase.
  const startDrag = (e, d) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", ""); setDrag(d); };
  const endDrag = () => { setDrag(null); setOver(null); };
  const overPhase = (e, pi) => { if (drag?.kind !== "phase") return; e.preventDefault(); if (over?.pi !== pi || over?.kind !== "phase") setOver({ kind: "phase", pi }); };
  // Sur une carte : moitié haute = devant elle, moitié basse = après elle (les espaces entre cartes ne comptent pas).
  const overEtape = (e, pi, ei, onCard) => {
    if (drag?.kind !== "etape") return;
    e.preventDefault(); e.stopPropagation();
    let at = ei;
    if (onCard) { const r = e.currentTarget.getBoundingClientRect(); if (e.clientY > r.top + r.height / 2) at = ei + 1; }
    if (over?.kind !== "etape" || over.pi !== pi || over.ei !== at) setOver({ kind: "etape", pi, ei: at });
  };
  const drop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (drag?.kind === "phase" && over?.kind === "phase") movePhase(drag.pi, over.pi);
    if (drag?.kind === "etape" && over?.kind === "etape") moveEtape({ pi: drag.pi, ei: drag.ei }, { pi: over.pi, ei: over.ei });
    endDrag();
  };

  if (!meta) return null;
  let sel = null;
  phases.forEach((ph, pi) => ph.etapes.forEach((e, ei) => { if (e.id === selected) sel = { e, pi, ei }; }));
  const line = <div className="h-0.5 rounded-full bg-marine" aria-hidden="true" />;

  return (
    <div data-testid="pwf-editor" className={sel ? "md:pr-[400px]" : ""}>
      <button type="button" onClick={leave} data-testid="pwf-back" className="text-sm text-marine hover:text-bordeaux flex items-center gap-1.5 mb-3">
        <Icons.ArrowLeft size={16} /> Workflows des prestations
      </button>
      <PageTitle overline={`Workflow · ${meta.type_label}`} title={name.trim() || "Nouveau workflow"} />

      <label className="text-sm block mb-6 max-w-xl">Nom du workflow
        <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} data-testid="pwf-name" placeholder="Ex. Site vitrine express" className="inp mt-1" />
      </label>

      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2">Phases</div>
      <div className="flex flex-col md:flex-row gap-4 md:items-start md:overflow-x-auto pb-2" data-testid="pwf-phase-list">
        {phases.map((ph, pi) => (
          <section key={ph.id} aria-label={ph.name || `Phase ${pi + 1}`} data-testid={`pwf-phase-${ph.id}`}
            onDragOver={(e) => overPhase(e, pi)} onDrop={drop}
            className={`md:flex-[1_1_290px] md:min-w-[290px] border rounded-sm p-3 bg-creme transition-shadow ${over?.kind === "phase" && over.pi === pi && drag?.pi !== pi ? "border-marine ring-2 ring-marine/20" : "border-marine/15"}`}>
            <div draggable onDragStart={(e) => startDrag(e, { kind: "phase", pi })} onDragEnd={endDrag}
              data-testid={`pwf-phase-handle-${ph.id}`} className="flex items-center gap-2 mb-3 md:cursor-grab md:active:cursor-grabbing">
              <Icons.GripVertical size={16} className="text-muet shrink-0 hidden md:block" />
              <span className="text-xs text-marine/80 shrink-0">{pi + 1}.</span>
              <input value={ph.name} onChange={(e) => edit((prev) => prev.map((x, i) => (i === pi ? { ...x, name: e.target.value } : x)))}
                data-testid={`pwf-phase-name-${ph.id}`} placeholder="Nom de la phase" aria-label="Nom de la phase"
                className="flex-1 min-w-0 border border-marine/20 rounded-sm p-1.5 text-sm font-medium text-marine bg-white" />
              <button type="button" onClick={() => removePhase(pi)} data-testid={`pwf-phase-remove-${ph.id}`} className="text-rouge shrink-0" title="Supprimer la phase" aria-label="Supprimer la phase">
                <Icons.Trash2 size={16} />
              </button>
            </div>
            <div className="space-y-2 min-h-[2.5rem]" onDragOver={(e) => overEtape(e, pi, ph.etapes.length, false)} onDrop={drop}>
              {ph.etapes.map((et, ei) => {
                const tpl = et.form_template_id ? tplOf(et.form_template_id) : null;
                const on = et.id === selected;
                return (
                  <React.Fragment key={et.id}>
                    {over?.kind === "etape" && over.pi === pi && over.ei === ei && line}
                    <div role="button" tabIndex={0} draggable onDragStart={(e) => startDrag(e, { kind: "etape", pi, ei })} onDragEnd={endDrag}
                      onDragOver={(e) => overEtape(e, pi, ei, true)} onDrop={drop}
                      onClick={() => setSelected(on ? null : et.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(on ? null : et.id); } }}
                      aria-pressed={on} data-testid={`pwf-etape-${et.id}`}
                      className={`border rounded-sm p-2.5 bg-sable cursor-pointer md:cursor-grab transition-opacity ${on ? "border-marine ring-2 ring-marine/30" : "border-marine/10 hover:border-marine/30"} ${drag?.kind === "etape" && drag.pi === pi && drag.ei === ei ? "opacity-40" : ""}`}>
                      <div className="flex items-start gap-2">
                        <Icons.GripVertical size={16} className="text-muet shrink-0 mt-0.5 hidden md:block" />
                        <span className="text-sm text-marine flex-1 min-w-0">{ei + 1}. {et.name}</span>
                      </div>
                      {(et.blocking || et.form_template_id) && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5 md:pl-6">
                          {et.blocking && <span className="text-[9px] uppercase tracking-wider whitespace-nowrap bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm">Étape obligatoire</span>}
                          {et.form_template_id && (tpl?.status === "active"
                            ? <span className="text-[9px] uppercase tracking-wider bg-creme text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm" data-testid={`pwf-etape-form-${et.id}`}>Formulaire · {tpl.name}</span>
                            : <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${INTENT.attente.cls}`} data-testid={`pwf-etape-form-warning-${et.id}`}>{tpl ? "Modèle de formulaire archivé" : "Modèle de formulaire introuvable"}</span>)}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {over?.kind === "etape" && over.pi === pi && over.ei === ph.etapes.length && line}
              {ph.etapes.length === 0 && <p className="text-xs text-marine/80 italic">Phase vide — ajoutez une étape, ou glissez-en une ici.</p>}
            </div>
            <div className="flex gap-2 mt-3">
              <input value={newEtape[pi] || ""} onChange={(e) => setNewEtape({ ...newEtape, [pi]: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEtape(pi); } }}
                data-testid={`pwf-phase-new-etape-${ph.id}`} placeholder="Ex. Brief créatif" aria-label="Nouvelle étape"
                className="flex-1 min-w-0 border border-marine/20 rounded-sm p-1.5 text-xs bg-white" />
              <button type="button" onClick={() => addEtape(pi)} data-testid={`pwf-phase-add-etape-${ph.id}`} className="bg-bordeaux text-white rounded-sm px-2 text-xs shrink-0">Ajouter</button>
            </div>
          </section>
        ))}
        <div className="md:flex-[0_0_240px] md:min-w-[240px] border-[1.5px] border-dashed border-marine/30 rounded-sm p-3" data-testid="pwf-new-phase-col">
          <div className="text-[11px] uppercase tracking-[0.08em] text-marine/80 mb-2">Nouvelle phase</div>
          <input value={newPhase} onChange={(e) => setNewPhase(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPhase(); } }}
            data-testid="pwf-new-phase" placeholder="Ex. Conception" aria-label="Nouvelle phase" className="w-full border border-marine/20 rounded-sm p-2 text-sm bg-white" />
          <button type="button" onClick={addPhase} data-testid="pwf-add-phase" className="w-full mt-2 bg-marine text-white rounded-sm px-3 py-2 text-sm">Ajouter une phase</button>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={leave} data-testid="pwf-cancel" className="px-4 py-2 text-sm text-marine/80">Annuler</button>
        <button type="button" onClick={save} disabled={saving} data-testid="pwf-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover disabled:opacity-50">Enregistrer</button>
      </div>

      {sel && (
        <aside aria-label="Réglages de l'étape" data-testid="pwf-panel"
          className="fixed z-40 inset-x-0 bottom-0 top-16 md:left-auto md:w-[400px] bg-sable md:border-l border-marine/10 shadow-xl p-6 overflow-y-auto text-marine">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-[0.2em] text-bordeaux font-semibold truncate">Étape · {phases[sel.pi].name || `Phase ${sel.pi + 1}`}</div>
            <button type="button" onClick={() => setSelected(null)} data-testid="pwf-panel-close" aria-label="Fermer les réglages de l'étape" className="text-marine/80 hover:text-marine"><Icons.X size={20} /></button>
          </div>
          <h2 className="font-display text-xl mt-1 mb-5 break-words">{sel.e.name || "Étape sans nom"}</h2>

          <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5" htmlFor="pwf-panel-name">Nom de l'étape</label>
          <input id="pwf-panel-name" value={sel.e.name} onChange={(e) => setEtape(sel.e.id, { name: e.target.value })} data-testid="pwf-panel-name" className="inp mb-4" />

          <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5" htmlFor="pwf-panel-desc">Description</label>
          <textarea id="pwf-panel-desc" rows={3} value={sel.e.description} onChange={(e) => setEtape(sel.e.id, { description: e.target.value })} data-testid="pwf-panel-desc"
            placeholder="Visible sur le portail client…" className="inp mb-4" />

          <div className="text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Étape obligatoire</div>
          <div className="flex items-center gap-3">
            <Switch checked={sel.e.blocking} onChange={() => setEtape(sel.e.id, { blocking: !sel.e.blocking })} testid="pwf-panel-blocking" title="Étape obligatoire" />
            <span className="text-sm">{sel.e.blocking ? "Oui" : "Non"}</span>
          </div>
          <p className="text-xs text-marine/80 mt-1.5 mb-4">Les étapes suivantes de la phase ne démarrent pas tant que celle-ci n'est pas validée.</p>

          <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5" htmlFor="pwf-panel-form">Formulaire</label>
          <select id="pwf-panel-form" value={sel.e.form_template_id} onChange={(e) => setEtape(sel.e.id, { form_template_id: e.target.value })} data-testid="pwf-panel-form" className="inp">
            <option value="">Aucun formulaire</option>
            {templates.filter((t) => t.status === "active" || t.id === sel.e.form_template_id).map((t) => (
              <option key={t.id} value={t.id}>{t.status === "active" ? t.name : `${t.name} (archivé)`}</option>
            ))}
            {sel.e.form_template_id && !tplOf(sel.e.form_template_id) && <option value={sel.e.form_template_id}>Modèle introuvable</option>}
          </select>
          {sel.e.form_template_id && tplOf(sel.e.form_template_id)?.status !== "active" && (
            <p className={`text-xs mt-1.5 px-2 py-1 rounded-sm ${INTENT.attente.cls}`} data-testid="pwf-panel-form-warning">Modèle de formulaire archivé : les nouveaux projets se créeront sans ce formulaire. Choisissez-en un autre ou retirez-le.</p>
          )}
          <p className="text-xs text-marine/80 mt-1.5 mb-4">Copié dans chaque nouveau projet créé avec ce workflow ; le client le voit quand l'étape démarre. Les projets déjà créés ne changent pas.</p>

          <div className="text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Déplacer</div>
          <div className="flex flex-wrap gap-2 mb-6">
            <button type="button" disabled={sel.ei === 0} onClick={() => moveEtape({ pi: sel.pi, ei: sel.ei }, { pi: sel.pi, ei: sel.ei - 1 })} data-testid="pwf-panel-up"
              className="border border-marine/30 rounded-sm px-2.5 py-1.5 text-xs flex items-center gap-1 disabled:opacity-40"><Icons.ArrowUp size={14} /> Monter</button>
            <button type="button" disabled={sel.ei === phases[sel.pi].etapes.length - 1} onClick={() => moveEtape({ pi: sel.pi, ei: sel.ei }, { pi: sel.pi, ei: sel.ei + 2 })} data-testid="pwf-panel-down"
              className="border border-marine/30 rounded-sm px-2.5 py-1.5 text-xs flex items-center gap-1 disabled:opacity-40"><Icons.ArrowDown size={14} /> Descendre</button>
            <select value={sel.pi} onChange={(e) => { const to = Number(e.target.value); moveEtape({ pi: sel.pi, ei: sel.ei }, { pi: to, ei: phases[to].etapes.length }); }}
              data-testid="pwf-panel-phase" aria-label="Phase de l'étape" className="border border-marine/30 rounded-sm px-2 py-1.5 text-xs bg-white">
              {/* Libellé d'un seul tenant : en développement, un outil d'annotation enveloppe la partie variable d'un texte
                  mixte (« Phase : {nom} ») dans un <span>, interdit dans une option. */}
              {phases.map((ph, i) => <option key={ph.id} value={i}>{`Phase : ${ph.name || `Phase ${i + 1}`}`}</option>)}
            </select>
          </div>

          <button type="button" onClick={() => removeEtape(sel.e.id)} data-testid="pwf-panel-remove" className="text-sm text-rouge flex items-center gap-1.5"><Icons.Trash2 size={16} /> Supprimer l'étape</button>
        </aside>
      )}
    </div>
  );
}
